/**
 * verify-store.ts — persists the OOB verification state (pinned identity keys +
 * which members a human has verified) per room.
 *
 * This is the local trust anchor for E2EE code sharing. It holds PUBLIC keys only
 * (Ed25519 identity pubkeys) plus a local "I compared the safety number out of band"
 * decision — never any private key. It therefore lives SEPARATE from the keystore
 * (which holds private material and has its own integrity checksum) in a sibling file
 * `~/.aethereum/keys/<roomId>.verify.json`, so recording a verification never rewrites
 * the private keystore.
 *
 * Why it matters (F-1): the public-key directory is server-controlled, so pinning +
 * an out-of-band safety-number check is the ONLY thing that makes code sharing
 * resistant to an active, malicious server. Once a member's identity key is pinned
 * here, a later server substitution DIVERGES from the pin and is a hard block.
 */

import {
  mkdirSync,
  existsSync,
  readFileSync,
  openSync,
  writeSync,
  fsyncSync,
  closeSync,
  renameSync,
  unlinkSync,
} from "node:fs";
import { join, dirname } from "node:path";
import { homedir } from "node:os";
import { keysDir } from "./keystore.js";

export const VERIFY_STORE_VERSION = 1 as const;

/** A pinned member identity: the member id + its base64 Ed25519 identity pubkey. */
export interface PinnedIdentity {
  member: string;
  identityPub: string; // base64
}

export interface VerifyStoreData {
  version: typeof VERIFY_STORE_VERSION;
  roomId: string;
  /** Every member identity we have pinned (trust-on-first-use anchors). */
  pins: PinnedIdentity[];
  /** Member ids a human has confirmed via an out-of-band safety-number check. */
  verified: string[];
  /** The safety number that was compared when `verified` was last set (base10 blocks). */
  safetyNumber?: string;
  /** ms epoch of the last verification (for display only; never used for ordering). */
  verifiedAt?: number;
}

export function verifyStorePath(roomId: string, home: string = homedir()): string {
  return join(keysDir(home), `${roomId}.verify.json`);
}

export function loadVerifyStore(roomId: string, home: string = homedir()): VerifyStoreData {
  const path = verifyStorePath(roomId, home);
  if (!existsSync(path)) {
    return { version: VERIFY_STORE_VERSION, roomId, pins: [], verified: [] };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(readFileSync(path, "utf8"));
  } catch {
    // Unlike the keystore, this holds only public/derived state; a corrupt file is
    // safe to treat as "nothing verified yet" (the worst case is you re-verify).
    return { version: VERIFY_STORE_VERSION, roomId, pins: [], verified: [] };
  }
  const d = parsed as Partial<VerifyStoreData>;
  if (d.version !== VERIFY_STORE_VERSION || d.roomId !== roomId || !Array.isArray(d.pins) || !Array.isArray(d.verified)) {
    return { version: VERIFY_STORE_VERSION, roomId, pins: [], verified: [] };
  }
  return {
    version: VERIFY_STORE_VERSION,
    roomId,
    pins: d.pins.filter((p) => p && typeof p.member === "string" && typeof p.identityPub === "string"),
    verified: d.verified.filter((m) => typeof m === "string"),
    safetyNumber: typeof d.safetyNumber === "string" ? d.safetyNumber : undefined,
    verifiedAt: typeof d.verifiedAt === "number" ? d.verifiedAt : undefined,
  };
}

/** Atomic write (temp + fsync + rename), mode 0o600 (public keys, but keep it tidy). */
export function saveVerifyStore(data: VerifyStoreData, home: string = homedir()): void {
  const path = verifyStorePath(data.roomId, home);
  const dir = dirname(path);
  mkdirSync(dir, { recursive: true, mode: 0o700 });
  const tmp = join(dir, `.${data.roomId}.verify.${process.pid}.${Date.now()}.tmp`);
  const fd = openSync(tmp, "wx", 0o600);
  try {
    writeSync(fd, JSON.stringify(data, null, 2) + "\n");
    fsyncSync(fd);
  } finally {
    closeSync(fd);
  }
  try {
    renameSync(tmp, path);
  } catch (err) {
    try {
      unlinkSync(tmp);
    } catch {
      /* best effort */
    }
    throw err;
  }
}
